import { CmsStoreFieldData } from "@/types/location";

export interface ILocation {
  id: string;
  fieldData: CmsStoreFieldData;
  distance: number;
}

const BASE_URL = process.env.NEXT_PUBLIC_APP_BASE_URL ?? "";

export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const fetchLocations = async (): Promise<ILocation[]> => {
  const response = await fetch(`${BASE_URL}/api/webflow/locations`);
  if (!response.ok) {
    throw new Error("Failed to fetch locations");
  }
  return response.json();
};

export function sortLocationsByDistance(
  locations: ILocation[],
  latitude: number,
  longitude: number,
): ILocation[] {
  return locations
    .map((loc) => ({
      ...loc,
      distance: parseFloat(
        calculateDistance(
          latitude,
          longitude,
          loc.fieldData.latitude,
          loc.fieldData.longitude,
        ).toFixed(1),
      ),
    }))
    .sort((a, b) => a.distance - b.distance);
}

export function filterLocationsByCoordinates(
  locations: ILocation[],
  latitude: number,
  longitude: number,
  radiusMiles = 50,
): ILocation[] {
  return locations
    .map((loc) => ({
      ...loc,
      distance: parseFloat(
        calculateDistance(
          latitude,
          longitude,
          loc.fieldData.latitude,
          loc.fieldData.longitude,
        ).toFixed(1),
      ),
    }))
    .filter((loc) => loc.distance <= radiusMiles)
    .sort((a, b) => a.distance - b.distance);
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export function filterLocationsInBounds(
  locations: ILocation[],
  bounds: MapBounds,
  centerLat: number,
  centerLng: number,
): ILocation[] {
  return locations
    .filter((loc) => {
      const lat = loc.fieldData.latitude;
      const lng = loc.fieldData.longitude;
      if (lat == null || lng == null) return false;
      return (
        lat >= bounds.south &&
        lat <= bounds.north &&
        lng >= bounds.west &&
        lng <= bounds.east
      );
    })
    .map((loc) => ({
      ...loc,
      distance: parseFloat(
        calculateDistance(
          centerLat,
          centerLng,
          loc.fieldData.latitude,
          loc.fieldData.longitude,
        ).toFixed(1),
      ),
    }))
    .sort((a, b) => a.distance - b.distance);
}

export const fetchLocationsByCoordinates = async (
  latitude: number,
  longitude: number,
  radiusMiles = 50,
): Promise<ILocation[]> => {
  const all = await fetchLocations();
  return filterLocationsByCoordinates(all, latitude, longitude, radiusMiles);
};
